import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Gallery.css';

interface GalleryImage {
  src: string;
  alt: string;
  caption: string;
}

interface GalleryProps {
  title: string;
  subtitle: string;
  description: React.ReactNode;
  images: GalleryImage[];
  footerText?: string;
  footerSubText?: string;
}

const Gallery: React.FC<GalleryProps> = ({
  title,
  subtitle,
  description,
  images,
  footerText = '© 2025 जीवन आधार फाऊंडेशन. सर्व हक्क राखीव.',
  footerSubText = 'संस्थापक आणि अध्यक्ष: श्री. किशोर सलगर'
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean[]>(Array(images.length).fill(true));
  const lightboxRef = useRef<HTMLDivElement>(null);

  // Handle image load events
  const handleImageLoad = (index: number) => {
    setIsLoading(prev => {
      const newState = [...prev];
      newState[index] = false;
      return newState;
    });
  };

  // Open lightbox with specific image
  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling
  };

  // Close lightbox
  const closeLightbox = () => {
    setIsLightboxOpen(false);
    document.body.style.overflow = ''; // Enable scrolling
  };

  // Navigate to previous image
  const prevImage = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex <= 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  // Navigate to next image
  const nextImage = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex >= images.length - 1 ? 0 : prevIndex + 1
    );
  }, [images.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isLightboxOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, prevImage, nextImage]);

  // Handle click outside lightbox content to close
  const handleLightboxBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeLightbox();
    }
  };

  // Handle touch events for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  // Focus trap for lightbox (accessibility)
  useEffect(() => {
    if (!isLightboxOpen || !lightboxRef.current) return;
    
    const lightbox = lightboxRef.current;
    const focusableElements = lightbox.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };
    
    lightbox.addEventListener('keydown', handleTabKey);
    firstElement?.focus();
    
    return () => {
      lightbox.removeEventListener('keydown', handleTabKey);
    };
  }, [isLightboxOpen]);

  // Lazy loading with Intersection Observer
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const handleIntersect = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const dataSrc = img.getAttribute('data-src');
          
          if (dataSrc) {
            img.src = dataSrc;
            img.removeAttribute('data-src');
          }
          
          observer.unobserve(img);
        }
      });
    };
    
    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    
    const imgElements = document.querySelectorAll('.gallery-item img[data-src]');
    imgElements.forEach(img => observer.observe(img));
    
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="gallery-container">
      {/* Header Section */}
      {/* <header className="gallery-header">
        <div className="header-content">
          <h1>{title}</h1>
          <p className="subtitle">{subtitle}</p>
        </div>
      </header> */}
      
      {/* Description Section */}
      {/* <section className="gallery-description">
        <div className="description-content">
          {description}
        </div>
      </section> */}
      
      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="gallery-heading">
          <h2>छायाचित्र प्रदर्शनी</h2>
        </div>
        
        <div className="gallery-grid">
          {images.map((image, index) => (
            <div 
              key={index} 
              className={`gallery-item ${isLoading[index] ? 'loading' : ''}`}
              onClick={() => openLightbox(index)}
              tabIndex={0}
              role="button"
              aria-label={`View image: ${image.caption}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  openLightbox(index);
                  e.preventDefault();
                }
              }}
            >
              <img 
                src={image.src} 
                alt={image.alt} 
                loading="lazy" 
                onLoad={() => handleImageLoad(index)}
                data-src={image.src}
              />
              <div className="gallery-caption">{image.caption}</div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Lightbox */}
      {isLightboxOpen && (
        <div 
          className="lightbox" 
          onClick={handleLightboxBackdropClick}
          ref={lightboxRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <button 
            className="lightbox-close" 
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            &times;
          </button>
          <button 
            className="lightbox-prev" 
            onClick={prevImage}
            aria-label="Previous image"
          >
            &#10094;
          </button>
          <button 
            className="lightbox-next" 
            onClick={nextImage}
            aria-label="Next image"
          >
            &#10095;
          </button>
          <div className="lightbox-content">
            <img 
              className="lightbox-image" 
              src={images[currentIndex].src} 
              alt={images[currentIndex].alt} 
            />
            <div className="lightbox-caption">
              {images[currentIndex].caption}
              <span className="lightbox-counter">
                {currentIndex + 1} / {images.length}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      
    </div>
  );
};

export default Gallery;
